import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { SweepPreview } from '../components/brand/SweepPreview';
import {
  Activity, Compass, Handshake, Megaphone, IdCard, FileText, Mic, FolderOpen,
  MessageSquare, DollarSign, ShieldCheck, Settings, Wallet, Landmark, Zap,
  Loader2, CheckCircle2, Download, Banknote, Receipt, HeartPulse, PiggyBank,
  Plus, X, Clock, ArrowDownToLine, Building2, Sparkles,
} from 'lucide-react';
import { DashboardShell } from '../components/brand/DashboardShell';
import { FeatureGate, UpgradePromptBanner } from '../components/brand/TierComponents';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { money } from '../lib/rcm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { icon: Activity, label: 'Dashboard', href: '/clinician/dashboard' },
  { icon: Compass, label: 'Practice Coach', href: '/clinician/coach', highlight: true },
  { icon: Handshake, label: 'Offers Inbox', href: '/clinician/offers' },
  { icon: Megaphone, label: 'Care Requests', href: '/clinician/care-requests' },
  { icon: IdCard, label: 'Practice Profile', href: '/clinician/dashboard#profile' },
  { icon: FileText, label: 'Clinical Notes', href: '/clinician/notes' },
  { icon: Mic, label: 'Voice Notes', href: '/clinician/voice-notes' },
  { icon: FolderOpen, label: 'Documents', href: '/clinician/documents' },
  { icon: MessageSquare, label: 'Messages', href: '/clinician/messages' },
  { icon: Wallet, label: 'Earnings & Payouts', href: '/clinician/payouts' },
  { icon: Landmark, label: 'Banking & Benefits', active: true },
  { icon: DollarSign, label: 'Billing & RCM', href: '/clinician/billing' },
  { icon: ShieldCheck, label: 'Privacy & Data', href: '/privacy/dashboard' },
  { icon: Settings, label: 'Settings', href: '#' },
];

const DemoRail = () => (
  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-bold" data-testid="banking-demo-rail">
    <Zap className="w-3 h-3" /> Demo rail · connect Stripe to enable real payouts
  </div>
);

const LiveRail = () => (
  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-bold" data-testid="banking-live-rail">
    <CheckCircle2 className="w-3 h-3" /> Stripe Connect · live payouts
  </div>
);

const CATEGORY_ICON = {
  Health: HeartPulse, Dental: HeartPulse, Vision: HeartPulse,
  'Income protection': ShieldCheck, Liability: ShieldCheck, Retirement: PiggyBank,
};

export default function ClinicianBanking() {
  const { getAuthHeader } = useAuth();
  const [tab, setTab] = useState('banking');

  const [bank, setBank] = useState(null);
  const [tax, setTax] = useState(null);
  const [benefits, setBenefits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // bank-link form
  const [linkForm, setLinkForm] = useState({ account_holder: '', bank_name: '', account_number: '', routing_number: '', account_type: 'checking' });
  // withdraw form
  const [wd, setWd] = useState({ amount: '', method: 'standard' });
  // w9 form
  const [w9, setW9] = useState({ legal_name: '', tax_id: '', business_type: 'individual', address: '' });

  const loadAll = useCallback(async () => {
    try {
      const [b, t, bn] = await Promise.all([
        axios.get(`${API}/clinician/banking`, getAuthHeader()),
        axios.get(`${API}/clinician/tax/1099`, getAuthHeader()),
        axios.get(`${API}/clinician/benefits`, getAuthHeader()),
      ]);
      setBank(b.data); setTax(t.data); setBenefits(bn.data);
    } catch (e) {
      toast.error('Could not load financial hub');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const linkBank = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/clinician/banking/link`, linkForm, getAuthHeader());
      toast.success(data.message || 'Bank linked');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not link bank');
    } finally { setBusy(false); }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      const body = { method: wd.method };
      if (wd.amount) body.amount = parseFloat(wd.amount);
      const { data } = await axios.post(`${API}/clinician/banking/payout`, body, getAuthHeader());
      toast.success(data.message || 'Payout requested');
      setWd({ amount: '', method: 'standard' });
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not request payout');
    } finally { setBusy(false); }
  };

  // Start Stripe Connect Express onboarding: backend creates the account + a
  // one-time hosted link, we redirect the clinician to it. After they finish
  // they're returned to /clinician/banking?stripe=return → we re-fetch state.
  const startStripeOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/clinician/banking/connect/onboard`, {}, getAuthHeader());
      if (data?.onboarding_url) {
        toast.success('Redirecting to Stripe to verify your identity & bank…');
        window.location.href = data.onboarding_url;
        return;
      }
      toast.error('Stripe did not return an onboarding URL');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start Stripe onboarding');
    } finally { setBusy(false); }
  };

  // After Stripe redirects back, re-sync state then strip the query param.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('stripe');
    if (flag === 'return' || flag === 'refresh') {
      (async () => {
        try {
          await axios.get(`${API}/clinician/banking/connect/status`, getAuthHeader());
          await loadAll();
          if (flag === 'return') toast.success('Stripe Connect updated — real payouts are now live.');
        } catch { /* silent */ }
        const url = new URL(window.location.href);
        url.searchParams.delete('stripe');
        window.history.replaceState({}, '', url.toString());
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitW9 = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/clinician/tax/w9`, w9, getAuthHeader());
      toast.success('W-9 saved');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save W-9');
    } finally { setBusy(false); }
  };

  const downloadWorksheet = async (year) => {
    try {
      const res = await axios.get(`${API}/clinician/tax/1099/statement?year=${year}`, { ...getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `caraloom-1099-NEC-${year}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('1099-NEC worksheet downloaded');
    } catch (e) {
      toast.error('Could not download worksheet');
    }
  };

  const enroll = async (planId) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/clinician/benefits/enroll`, { plan_id: planId }, getAuthHeader());
      toast.success(data.message || 'Enrolled');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not enroll');
    } finally { setBusy(false); }
  };

  const cancelBenefit = async (enrollmentId) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/clinician/benefits/enroll/${enrollmentId}`, getAuthHeader());
      toast.success('Coverage cancelled');
      await loadAll();
    } catch (e) {
      toast.error('Could not cancel');
    } finally { setBusy(false); }
  };

  const bal = bank?.balance || {};
  const linked = bank?.banking?.bank_linked;
  const connect = bank?.connect || {};
  const stripeLive = !!connect.ready;
  const instantFee = bank?.instant_fee_pct || 0.015;
  const wdAmount = wd.amount ? parseFloat(wd.amount) : bal.available || 0;
  const wdFee = wd.method === 'instant' ? +(wdAmount * instantFee).toFixed(2) : 0;
  const enrolledIds = benefits?.enrolled_plan_ids || [];
  const enrollMap = Object.fromEntries((benefits?.enrollments || []).map((e) => [e.plan_id, e]));

  return (
    <DashboardShell
      navItems={NAV}
      title="Banking & Benefits"
      subtitle="Your money, taxes, and benefits — the financial back office for an independent clinical practice."
      testId="clinician-banking-page"
    >
      {loading ? (
        <div className="grid sm:grid-cols-3 gap-4">{[1, 2, 3].map((i) => <div key={i} className="cn-card h-28 animate-pulse bg-cn-bg/60" />)}</div>
      ) : (
        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="bg-cn-soft/60 p-1 rounded-xl">
            <TabsTrigger value="banking" className="rounded-lg" data-testid="banking-tab"><Landmark className="w-4 h-4 mr-2" /> Banking</TabsTrigger>
            <TabsTrigger value="taxes" className="rounded-lg" data-testid="taxes-tab"><Receipt className="w-4 h-4 mr-2" /> Taxes (1099)</TabsTrigger>
            <TabsTrigger value="benefits" className="rounded-lg" data-testid="benefits-tab"><HeartPulse className="w-4 h-4 mr-2" /> Benefits</TabsTrigger>
          </TabsList>

          {/* ============ BANKING ============ */}
          <TabsContent value="banking" className="space-y-6">
            <UpgradePromptBanner monthlyBillings={(bal.released_total || 0) + (bal.pending_escrow || 0)} />

            {/* Sweep preview — Blueprint #29 (next-sweep envelope) */}
            <SweepPreview getAuthHeader={getAuthHeader} />

            {/* Balance hero */}
            <div className="rounded-xl p-6 bg-cn-primary text-white" data-testid="banking-balance-hero">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-white/70">Available to withdraw</p>
                  <p className="font-display font-bold text-4xl mt-1" data-testid="banking-available">{money(bal.available)}</p>
                </div>
                {stripeLive ? <LiveRail /> : <DemoRail />}
              </div>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div><p className="text-[11px] uppercase tracking-wider text-white/60 font-semibold">Awaiting verification</p><p className="font-display font-bold text-lg mt-0.5" data-testid="banking-awaiting-verification">{money(bal.pending_escrow)}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-white/60 font-semibold">Lifetime released</p><p className="font-display font-bold text-lg mt-0.5">{money(bal.released_total)}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-white/60 font-semibold">Withdrawn</p><p className="font-display font-bold text-lg mt-0.5">{money(bal.withdrawn)}</p></div>
              </div>
            </div>

            {/* Stripe Connect Express onboarding — the path to REAL instant payouts */}
            {!stripeLive && (
              <div className="cn-card p-6" data-testid="stripe-connect-card">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-cn-primary text-white flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display font-bold text-lg text-cn-text">
                      {connect?.account_id ? 'Finish Stripe verification to unlock real payouts' : 'Switch on real instant payouts'}
                    </h3>
                    <p className="text-sm text-cn-text-muted mt-1 leading-relaxed">
                      {connect?.account_id
                        ? 'Stripe still needs a few details before money can move. The link takes ~3 minutes.'
                        : 'Connect your identity + a debit card or bank with Stripe (Express, ~3 min). Once approved, "Cash out" sends real money to your card in seconds.'}
                    </p>
                    {connect?.requirements_due?.length > 0 && (
                      <div className="mt-2 text-[11px] text-amber-700">
                        <strong>Outstanding:</strong> {connect.requirements_due.slice(0, 4).join(' · ')}
                      </div>
                    )}
                    <div className="mt-4 flex items-center gap-2 text-[11px] text-cn-text-muted">
                      <ShieldCheck className="w-3.5 h-3.5" /> Stripe holds the verification — CaraLoom never sees your SSN or full bank number.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={startStripeOnboarding}
                    disabled={busy}
                    className="cn-btn cn-btn-primary text-sm whitespace-nowrap"
                    data-testid="stripe-connect-onboard-btn"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> :
                      (connect?.account_id ? 'Finish setup' : 'Connect Stripe')}
                  </button>
                </div>
              </div>
            )}

            {!linked ? (
              /* Link bank */
              <form onSubmit={linkBank} className="cn-card p-6 space-y-4" data-testid="bank-link-form">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-cn-primary" />
                  <h3 className="font-display font-bold text-cn-text dark:text-white">Link your payout bank account</h3>
                </div>
                <p className="text-sm text-cn-text-muted">We store only the last 4 digits. Settlement is simulated in this demo environment.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input required placeholder="Account holder name" value={linkForm.account_holder} onChange={(e) => setLinkForm({ ...linkForm, account_holder: e.target.value })} className="cn-input" data-testid="bank-account-holder" />
                  <input required placeholder="Bank name" value={linkForm.bank_name} onChange={(e) => setLinkForm({ ...linkForm, bank_name: e.target.value })} className="cn-input" data-testid="bank-name" />
                  <input required placeholder="Account number" value={linkForm.account_number} onChange={(e) => setLinkForm({ ...linkForm, account_number: e.target.value.replace(/\D/g, '') })} className="cn-input" data-testid="bank-account-number" />
                  <input required placeholder="Routing number (9 digits)" value={linkForm.routing_number} onChange={(e) => setLinkForm({ ...linkForm, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) })} className="cn-input" data-testid="bank-routing-number" />
                  <Select value={linkForm.account_type} onValueChange={(v) => setLinkForm({ ...linkForm, account_type: v })}>
                    <SelectTrigger className="w-full" data-testid="bank-account-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="checking">Checking</SelectItem>
                      <SelectItem value="savings">Savings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button type="submit" disabled={busy} className="cn-btn cn-btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="bank-link-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Landmark className="w-4 h-4" />} Link account
                </button>
              </form>
            ) : (
              /* Linked + withdraw */
              <div className="grid lg:grid-cols-2 gap-4">
                <div className="cn-card p-6" data-testid="linked-bank-card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-xl bg-cn-primary/10 text-cn-primary flex items-center justify-center"><Banknote className="w-5 h-5" /></span>
                      <div>
                        <p className="font-display font-bold text-cn-text dark:text-white">{bank.banking.bank_name}</p>
                        <p className="text-sm text-cn-text-muted">{bank.banking.account_type || 'checking'} ••{bank.banking.account_last4}</p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600"><CheckCircle2 className="w-4 h-4" /> Linked</span>
                  </div>
                </div>

                <div className="cn-card p-6 space-y-3" data-testid="withdraw-card">
                  <h3 className="font-display font-bold text-cn-text dark:text-white flex items-center gap-2"><ArrowDownToLine className="w-4 h-4" /> Withdraw to bank</h3>
                  <input
                    type="number" min="0" step="0.01" placeholder={`Amount (max ${money(bal.available)})`}
                    value={wd.amount} onChange={(e) => setWd({ ...wd, amount: e.target.value })}
                    className="cn-input w-full" data-testid="withdraw-amount"
                  />
                  <div className="flex gap-2">
                    {[['standard', 'Standard · free · ~2 days'], ['instant', `Instant · ${(instantFee * 100).toFixed(1)}% fee`]].map(([m, label]) => {
                      const btn = (
                        <button key={m} type="button" onClick={() => setWd({ ...wd, method: m })}
                          data-testid={`withdraw-method-${m}`}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${wd.method === m ? 'bg-cn-primary text-white border-cn-primary' : 'bg-cn-soft/50 text-cn-text-muted border-cn-border'}`}>
                          {label}
                        </button>
                      );
                      if (m === 'instant') {
                        return (
                          <FeatureGate
                            key={m}
                            feature="instant_payouts"
                            fallback={
                              <button
                                key={m}
                                type="button"
                                disabled
                                title="Instant payouts unlock with the Pro plan"
                                data-testid={`withdraw-method-${m}-locked`}
                                className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold border border-dashed border-cn-border bg-cn-soft/30 text-cn-text-muted/70 cursor-not-allowed inline-flex items-center justify-center gap-1.5"
                              >
                                <Sparkles className="w-3 h-3" />
                                Instant · Pro
                              </button>
                            }
                          >
                            {btn}
                          </FeatureGate>
                        );
                      }
                      return btn;
                    })}
                  </div>
                  {wd.method === 'instant' && wdAmount > 0 && (
                    <p className="text-xs text-cn-text-muted">Fee {money(wdFee)} · you receive <strong className="text-cn-text dark:text-white">{money(wdAmount - wdFee)}</strong></p>
                  )}
                  <button onClick={withdraw} disabled={busy || (bal.available || 0) <= 0} className="cn-btn cn-btn-primary w-full justify-center disabled:opacity-50" data-testid="withdraw-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowDownToLine className="w-4 h-4" />} Withdraw {wd.amount ? money(wdAmount) : money(bal.available)}
                  </button>
                </div>
              </div>
            )}

            {/* Payout history */}
            <div className="cn-card overflow-hidden" data-testid="payout-history">
              <div className="px-5 py-4 border-b border-cn-border"><h3 className="font-display font-bold text-cn-text dark:text-white">Payout history</h3></div>
              {(bank?.payouts || []).length === 0 ? (
                <div className="px-5 py-10 text-center text-cn-text-muted" data-testid="payout-history-empty">No payouts yet. Withdraw your available balance to get started.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[11px] uppercase tracking-wider text-cn-text-muted border-b border-cn-border">
                      <th className="px-5 py-3 font-bold">Requested</th><th className="px-5 py-3 font-bold">Method</th>
                      <th className="px-5 py-3 font-bold text-right">Amount</th><th className="px-5 py-3 font-bold text-right">Fee</th>
                      <th className="px-5 py-3 font-bold text-right">Net</th><th className="px-5 py-3 font-bold">Status</th>
                    </tr></thead>
                    <tbody>
                      {bank.payouts.map((p, i) => (
                        <tr key={p.id || i} className="border-b border-cn-border/60 last:border-0" data-testid={`payout-row-${i}`}>
                          <td className="px-5 py-3 text-cn-text-muted whitespace-nowrap">{(p.created_at || '').slice(0, 10)}</td>
                          <td className="px-5 py-3 capitalize">{p.method}</td>
                          <td className="px-5 py-3 text-right">{money(p.amount)}</td>
                          <td className="px-5 py-3 text-right text-red-600">{p.fee ? `−${money(p.fee)}` : '—'}</td>
                          <td className="px-5 py-3 text-right font-semibold text-cn-primary">{money(p.net)}</td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border ${p.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                              {p.status === 'paid' ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {p.status === 'paid' ? 'Paid' : 'In transit'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============ TAXES (1099-NEC) ============ */}
          <TabsContent value="taxes" className="space-y-6">
            {/* W-9 status */}
            <div className="cn-card p-6" data-testid="w9-card">
              <div className="flex items-center gap-2 mb-1">
                <IdCard className="w-5 h-5 text-cn-primary" />
                <h3 className="font-display font-bold text-cn-text dark:text-white">Taxpayer info (W-9)</h3>
                {tax?.w9_submitted && <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 ml-2" data-testid="w9-status-complete"><CheckCircle2 className="w-4 h-4" /> On file ••{tax?.recipient?.tax_id_last4}</span>}
              </div>
              {tax?.w9_submitted ? (
                <p className="text-sm text-cn-text-muted">{tax.recipient.legal_name} · {tax.recipient.business_type}. CaraLoom uses this to issue your official 1099-NEC.</p>
              ) : (
                <form onSubmit={submitW9} className="space-y-3 mt-2" data-testid="w9-form">
                  <p className="text-sm text-cn-text-muted">Add your taxpayer details so CaraLoom can prepare your year-end 1099-NEC. Only the last 4 of your TIN is stored.</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input required placeholder="Legal name" value={w9.legal_name} onChange={(e) => setW9({ ...w9, legal_name: e.target.value })} className="cn-input" data-testid="w9-legal-name" />
                    <input required placeholder="TIN (SSN or EIN)" value={w9.tax_id} onChange={(e) => setW9({ ...w9, tax_id: e.target.value.replace(/[^0-9]/g, '').slice(0, 9) })} className="cn-input" data-testid="w9-tax-id" />
                    <Select value={w9.business_type} onValueChange={(v) => setW9({ ...w9, business_type: v })}>
                      <SelectTrigger className="w-full" data-testid="w9-business-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="individual">Individual / Sole proprietor</SelectItem>
                        <SelectItem value="llc">LLC</SelectItem>
                        <SelectItem value="s_corp">S-Corporation</SelectItem>
                      </SelectContent>
                    </Select>
                    <input placeholder="Mailing address (optional)" value={w9.address} onChange={(e) => setW9({ ...w9, address: e.target.value })} className="cn-input" data-testid="w9-address" />
                  </div>
                  <button type="submit" disabled={busy} className="cn-btn cn-btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="w9-submit">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save W-9
                  </button>
                </form>
              )}
            </div>

            {/* Payer band */}
            <div className="cn-card p-4 flex flex-wrap gap-x-8 gap-y-1 text-sm" data-testid="payer-info">
              <span className="text-cn-text-muted">Payer: <strong className="text-cn-text dark:text-white">{tax?.payer?.name}</strong></span>
              <span className="text-cn-text-muted">Payer TIN: {tax?.payer?.tin}</span>
            </div>

            {/* Per-year 1099 cards */}
            {(tax?.years || []).length === 0 ? (
              <div className="cn-card p-10 text-center text-cn-text-muted" data-testid="tax-empty">No paid earnings yet. Your 1099-NEC summary appears here as you get paid.</div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {tax.years.map((y) => (
                  <div key={y.year} className="cn-card p-5" data-testid={`tax-year-${y.year}`}>
                    <div className="flex items-center justify-between">
                      <h4 className="font-display font-bold text-lg text-cn-text dark:text-white">Tax Year {y.year}</h4>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${y.threshold_met ? 'bg-cn-primary/10 text-cn-primary' : 'bg-slate-100 text-slate-500'}`} data-testid={`tax-threshold-${y.year}`}>
                        {y.threshold_met ? '1099-NEC issued' : 'Below $600'}
                      </span>
                    </div>
                    <div className="mt-3 space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-cn-text-muted">Box 1 — Nonemployee comp</span><span className="font-display font-bold text-cn-primary" data-testid={`tax-box1-${y.year}`}>{money(y.nonemployee_comp)}</span></div>
                      <div className="flex justify-between"><span className="text-cn-text-muted">Gross billed</span><span className="text-cn-text dark:text-white">{money(y.gross)}</span></div>
                      <div className="flex justify-between"><span className="text-cn-text-muted">Platform fees</span><span className="text-red-600">−{money(y.platform_fees)}</span></div>
                      <div className="flex justify-between"><span className="text-cn-text-muted">Paid visits</span><span className="text-cn-text dark:text-white">{y.visits}</span></div>
                    </div>
                    <button onClick={() => downloadWorksheet(y.year)} className="cn-btn cn-btn-ghost mt-4 w-full justify-center text-sm" data-testid={`tax-download-${y.year}`}>
                      <Download className="w-4 h-4" /> Download 1099-NEC worksheet
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ============ BENEFITS ============ */}
          <TabsContent value="benefits" className="space-y-6">
            <div className="cn-card p-5 flex items-center justify-between flex-wrap gap-3" data-testid="benefits-summary">
              <div>
                <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Your monthly benefits cost</p>
                <p className="font-display font-bold text-3xl text-cn-text dark:text-white mt-1" data-testid="benefits-monthly-total">{money(benefits?.monthly_total)}<span className="text-sm text-cn-text-muted font-normal">/mo</span></p>
              </div>
              <div className="text-sm text-cn-text-muted max-w-xs">Group rates negotiated for CaraLoom clinicians. <DemoRail /></div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(benefits?.plans || []).map((p) => {
                const Icon = CATEGORY_ICON[p.category] || HeartPulse;
                const isEnrolled = enrolledIds.includes(p.id);
                const enr = enrollMap[p.id];
                return (
                  <div key={p.id} className={`cn-card p-5 flex flex-col ${isEnrolled ? 'border-cn-primary' : ''}`} data-testid={`benefit-plan-${p.id}`}>
                    <div className="flex items-start justify-between">
                      <span className="w-10 h-10 rounded-xl bg-cn-primary/10 text-cn-primary flex items-center justify-center"><Icon className="w-5 h-5" /></span>
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-cn-soft text-cn-text-muted">{p.category}</span>
                    </div>
                    <h4 className="font-display font-bold text-cn-text dark:text-white mt-3">{p.name}</h4>
                    <p className="text-xs text-cn-text-muted">{p.carrier}</p>
                    <p className="text-sm text-cn-text-muted mt-2 flex-1">{p.summary}</p>
                    <ul className="mt-3 space-y-1">
                      {p.highlights.map((h, i) => (
                        <li key={i} className="text-xs text-cn-text-muted flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-cn-primary flex-shrink-0" /> {h}</li>
                      ))}
                    </ul>
                    <div className="mt-4 flex items-end justify-between">
                      <span className="font-display font-bold text-lg text-cn-text dark:text-white">{p.monthly_premium === 0 ? 'No premium' : <>{money(p.monthly_premium)}<span className="text-xs text-cn-text-muted font-normal">/mo</span></>}</span>
                    </div>
                    {isEnrolled ? (
                      <button onClick={() => cancelBenefit(enr?.id)} disabled={busy} className="cn-btn cn-btn-ghost mt-3 w-full justify-center text-sm border border-cn-border disabled:opacity-50" data-testid={`benefit-cancel-${p.id}`}>
                        <X className="w-4 h-4" /> Cancel coverage
                      </button>
                    ) : (
                      <button onClick={() => enroll(p.id)} disabled={busy} className="cn-btn cn-btn-primary mt-3 w-full justify-center text-sm disabled:opacity-50" data-testid={`benefit-enroll-${p.id}`}>
                        <Plus className="w-4 h-4" /> Enroll
                      </button>
                    )}
                    {isEnrolled && <span className="text-[11px] text-emerald-600 font-bold mt-2 inline-flex items-center gap-1" data-testid={`benefit-enrolled-${p.id}`}><CheckCircle2 className="w-3 h-3" /> Enrolled</span>}
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      )}
    </DashboardShell>
  );
}
